import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";
import Editor from "@monaco-editor/react";
import { Box, Button, Typography } from "@mui/material";
import { Code, AutoFixHigh } from "@mui/icons-material";
import { formatEmailHtmlSource } from "../../utils/formatEmailHtmlSource";

const MERGE_TAG_REGEX = /\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\}/g;

const EDITOR_OPTIONS = {
    language: "html",
    theme: "vs",
    fontFamily: "Consolas, 'Courier New', monospace",
    fontSize: 13,
    lineHeight: 20,
    minimap: { enabled: false },
    wordWrap: "on",
    wrappingIndent: "indent",
    automaticLayout: true,
    scrollBeyondLastLine: false,
    tabSize: 2,
    insertSpaces: true,
    renderLineHighlight: "line",
    renderWhitespace: "selection",
    bracketPairColorization: { enabled: true },
    guides: {
        indentation: true,
        bracketPairs: true,
    },
    formatOnPaste: false,
    formatOnType: false,
    folding: true,
    glyphMargin: false,
    lineNumbers: "on",
    padding: { top: 12, bottom: 12 },
    scrollbar: {
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10,
    },
};

/**
 * VS Code–style Raw HTML source editor (Monaco) with pretty-print formatting.
 */
const RawHtmlCodeEditor = forwardRef(
    (
        {
            value = "",
            onChange,
            onBlur,
            onFocusCaret,
            placeholder = "Paste or type your HTML here…",
            formatOnMount = true,
        },
        ref
    ) => {
        const editorRef = useRef(null);
        const monacoRef = useRef(null);
        const decorationIdsRef = useRef([]);
        const applyingExternalRef = useRef(false);
        const hasMountedFormatRef = useRef(false);

        const highlightMergeTags = useCallback((editor, monaco) => {
            if (!editor || !monaco) return;
            const model = editor.getModel();
            if (!model) return;

            const text = model.getValue();
            const ranges = [];
            MERGE_TAG_REGEX.lastIndex = 0;
            let match;
            while ((match = MERGE_TAG_REGEX.exec(text)) !== null) {
                const start = model.getPositionAt(match.index);
                const end = model.getPositionAt(match.index + match[0].length);
                ranges.push({
                    range: new monaco.Range(
                        start.lineNumber,
                        start.column,
                        end.lineNumber,
                        end.column
                    ),
                    options: {
                        inlineClassName: "raw-html-merge-tag",
                        stickiness:
                            monaco.editor.TrackedRangeStickiness
                                .NeverGrowsWhenTypingAtEdges,
                    },
                });
            }

            decorationIdsRef.current = editor.deltaDecorations(
                decorationIdsRef.current,
                ranges
            );
        }, []);

        const emitChange = useCallback(
            (next) => {
                onChange?.(next);
            },
            [onChange]
        );

        const formatDocument = useCallback(() => {
            const editor = editorRef.current;
            if (!editor) return "";

            const formatted = formatEmailHtmlSource(editor.getValue());
            applyingExternalRef.current = true;
            editor.setValue(formatted);
            applyingExternalRef.current = false;
            highlightMergeTags(editor, monacoRef.current);
            emitChange(formatted);
            return formatted;
        }, [emitChange, highlightMergeTags]);

        const setEditorValue = useCallback(
            (html, { format = false } = {}) => {
                const next = format
                    ? formatEmailHtmlSource(html)
                    : String(html || "");
                const editor = editorRef.current;

                if (editor) {
                    applyingExternalRef.current = true;
                    const current = editor.getValue();
                    if (current !== next) {
                        editor.setValue(next);
                    }
                    applyingExternalRef.current = false;
                    highlightMergeTags(editor, monacoRef.current);
                }

                emitChange(next);
                return next;
            },
            [emitChange, highlightMergeTags]
        );

        useImperativeHandle(
            ref,
            () => ({
                getValue: () => editorRef.current?.getValue() ?? String(value || ""),
                setValue: setEditorValue,
                formatDocument,
                focus: () => editorRef.current?.focus(),
                insertText: (text) => {
                    const editor = editorRef.current;
                    if (!editor || !text) return;

                    const selection = editor.getSelection();
                    const id = { major: 1, minor: 1 };
                    const op = {
                        identifier: id,
                        range: selection,
                        text: String(text),
                        forceMoveMarkers: true,
                    };
                    editor.executeEdits("insert-merge-tag", [op]);
                    editor.focus();
                    highlightMergeTags(editor, monacoRef.current);
                    emitChange(editor.getValue());
                },
                getCaret: () => {
                    const editor = editorRef.current;
                    if (!editor) return null;
                    const model = editor.getModel();
                    const selection = editor.getSelection();
                    if (!model || !selection) return null;
                    return {
                        type: "text",
                        start: model.getOffsetAt(selection.getStartPosition()),
                        end: model.getOffsetAt(selection.getEndPosition()),
                    };
                },
            }),
            [value, setEditorValue, formatDocument, emitChange, highlightMergeTags]
        );

        const handleMount = useCallback(
            (editor, monaco) => {
                editorRef.current = editor;
                monacoRef.current = monaco;

                monaco.editor.defineTheme("raw-html-light", {
                    base: "vs",
                    inherit: true,
                    rules: [],
                    colors: {
                        "editor.background": "#ffffff",
                        "editorLineNumber.foreground": "#9e9e9e",
                        "editor.selectionBackground": "#bbdefb88",
                    },
                });
                monaco.editor.setTheme("raw-html-light");

                editor.addAction({
                    id: "format-email-html",
                    label: "Format HTML",
                    keybindings: [
                        monaco.KeyMod.Alt |
                            monaco.KeyMod.Shift |
                            monaco.KeyCode.KeyF,
                    ],
                    run: () => {
                        formatDocument();
                    },
                });

                const initial = String(value || "");
                const next =
                    formatOnMount && initial.trim() && !hasMountedFormatRef.current
                        ? formatEmailHtmlSource(initial)
                        : initial;

                hasMountedFormatRef.current = true;
                applyingExternalRef.current = true;
                editor.setValue(next);
                applyingExternalRef.current = false;
                highlightMergeTags(editor, monaco);

                if (next !== initial) {
                    emitChange(next);
                }

                editor.onDidChangeCursorSelection(() => {
                    onFocusCaret?.();
                });

                editor.onDidBlurEditorWidget(() => {
                    onBlur?.(editor.getValue());
                });
            },
            [
                value,
                formatOnMount,
                formatDocument,
                highlightMergeTags,
                emitChange,
                onBlur,
                onFocusCaret,
            ]
        );

        // Sync external value changes (mode switch / load) without clobbering typing.
        useEffect(() => {
            const editor = editorRef.current;
            if (!editor) return;
            const external = String(value || "");
            const current = editor.getValue();
            if (external === current) {
                highlightMergeTags(editor, monacoRef.current);
                return;
            }
            if (applyingExternalRef.current) return;

            applyingExternalRef.current = true;
            editor.setValue(external);
            applyingExternalRef.current = false;
            highlightMergeTags(editor, monacoRef.current);
        }, [value, highlightMergeTags]);

        const isEmpty = !String(value || "").trim();

        return (
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    position: "relative",
                    "& .raw-html-merge-tag": {
                        backgroundColor: "#e3f2fd",
                        border: "1px solid #90caf9",
                        borderRadius: "3px",
                        padding: "0 2px",
                        fontWeight: 600,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        backgroundColor: "#fafafa",
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Code sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.2 }}
                        >
                            HTML source
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<AutoFixHigh sx={{ fontSize: 16 }} />}
                        onClick={formatDocument}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 1.5,
                            minWidth: "auto",
                        }}
                    >
                        Format
                    </Button>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                    {isEmpty && (
                        <Typography
                            variant="body2"
                            sx={{
                                position: "absolute",
                                zIndex: 1,
                                top: 12,
                                left: 52,
                                color: "text.disabled",
                                pointerEvents: "none",
                                fontFamily: "Consolas, monospace",
                                fontSize: 13,
                            }}
                        >
                            {placeholder}
                        </Typography>
                    )}
                    <Editor
                        height="100%"
                        defaultLanguage="html"
                        theme="vs"
                        options={EDITOR_OPTIONS}
                        onMount={handleMount}
                        onChange={(next) => {
                            if (applyingExternalRef.current) return;
                            const editor = editorRef.current;
                            if (editor) {
                                highlightMergeTags(editor, monacoRef.current);
                            }
                            emitChange(next ?? "");
                        }}
                        loading={
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                    fontSize: 13,
                                }}
                            >
                                Loading editor…
                            </Box>
                        }
                    />
                </Box>
            </Box>
        );
    }
);

RawHtmlCodeEditor.displayName = "RawHtmlCodeEditor";

export default RawHtmlCodeEditor;
